import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classNames from "classnames";
import { appendTimezone, timestampToDateString } from "../../../lib/date";
import { formatNumber } from "../../../lib/number";
import Metric from "../../Metric";
import ChartTooltipAction from "../../ChartTooltipAction";
import Tooltip from "..";
import AddNoteAction from "../AddNoteAction";
import EditNoteAction from "../EditNoteAction";
import Change from "../../Change";
import ViewSessionsAction from "../../ChartTooltipAction/ViewSessionsAction";
import CompareSessionsAction from "../../ChartTooltipAction/CompareSessionsAction";
import { getAlertsSeriesId, getDeploysSeriesId, getNotesSeriesId } from "../../../lib/series";
import style from "./style.module.scss";
const alertsId = getAlertsSeriesId();
const deploysId = getDeploysSeriesId();
const notesId = getNotesSeriesId();
export default function ChartTooltip(props) {
    var _a, _b, _c;
    const point = props.point;
    const series = point.series.userOptions;
    let tooltipTitle = null;
    let budgetPercentage = null;
    //Check if the series has a budget associated with it.
    const budget = (_a = series.settings) === null || _a === void 0 ? void 0 : _a.budget;
    const budgetThreshold = budget === null || budget === void 0 ? void 0 : budget.threshold;
    const budgetMetric = (_b = budget === null || budget === void 0 ? void 0 : budget.metric) === null || _b === void 0 ? void 0 : _b.qualified;
    if (budgetMetric === series.metricQualified && (budgetThreshold === null || budgetThreshold === void 0 ? void 0 : budgetThreshold.value) !== undefined) {
        //Tooltip is for a series with a budget.
        //Work out if it's an absolute or relative budget.
        if ((budgetThreshold === null || budgetThreshold === void 0 ? void 0 : budgetThreshold.type) === "absolute") {
            const reverseBudget = series.budgetReverse;
            const pointValue = point.y;
            const budgetValue = budgetThreshold.value;
            //Is the current point under or over budget.
            if (pointValue > budgetValue) {
                const changePer = Math.round((pointValue / budgetValue) * 100) - 100;
                const changeSuffix = "% Over Budget";
                let changeColor = "bad";
                if (reverseBudget) {
                    changeColor = "good";
                }
                budgetPercentage = (_jsx("div", { className: style["budget"], children: _jsx(Change, { value: changePer, suffix: changeSuffix, changeType: changeColor, noArrow: true, smallMode: true }) }));
            }
            else if (pointValue <= budgetValue) {
                const changePer = 100 - Math.round((pointValue / budgetValue) * 100);
                const changeSuffix = "% Under Budget";
                let changeColor = "good";
                if (reverseBudget) {
                    changeColor = "bad";
                }
                budgetPercentage = (_jsx("div", { className: style["budget"], children: _jsx(Change, { value: changePer, suffix: changeSuffix, changeType: changeColor, noArrow: true, smallMode: true }) }));
            }
        }
        else if ((budgetThreshold === null || budgetThreshold === void 0 ? void 0 : budgetThreshold.type) === "rate_of_change") {
            let changeVal = 0;
            if ((_c = point.difference) === null || _c === void 0 ? void 0 : _c.percentage) {
                changeVal = point.difference.percentage;
            }
            let changeColor = "good";
            if (point.status === "under") {
                changeColor = "good";
            }
            else if (point.status === "over") {
                changeColor = "bad";
            }
            else if (point.status === "no_cross") {
                changeColor = "neutral";
            }
            budgetPercentage = (_jsx("div", { className: style["budget"], children: _jsx(Change, { value: changeVal, suffix: "% change", changeType: changeColor, noArrow: true, smallMode: true }) }));
        }
    }
    const actionsList = [
        _jsx(ViewSessionsAction, { type: "timeseries", point: point }, "view-sessions"),
        _jsx(CompareSessionsAction, { type: "timeseries", point: point }, "compare-sessions"),
    ];
    if (point.test) {
        //Synthetic tool tip so display synthetic options.
        actionsList.push(_jsx(ChartTooltipAction, { icon: "eye", children: _jsx("a", { href: `${window.accountUrl}/test/${point.test}/`, children: "View Test" }) }), _jsx(ChartTooltipAction, { icon: "compare", children: _jsx("a", { href: `${window.accountUrl}/test/${point.test}/`, className: "compare__action", "data-action": "add", "data-wpt_id": point.test, children: "Compare Test" }) }), _jsx(ChartTooltipAction, { icon: "bookmark", children: _jsx("a", { href: `${window.accountUrl}/bookmarks/?wpt_id=${point.test}`, className: "bookmark__action", "data-action": "add", "data-bookmark-type": "syn", children: "Bookmark Test" }) }));
    }
    if (series.id !== notesId && series.id !== alertsId) {
        actionsList.push(_jsx(AddNoteAction, { point: point }));
    }
    if (series.id === notesId) {
        actionsList.push(_jsx(EditNoteAction, { point: point }));
    }
    if (series.id === deploysId) {
        actionsList.push(_jsx(ChartTooltipAction, { icon: "eye", children: _jsx("a", { href: `${window.accountUrl}/deployment/${point.options.id}/`, children: "View Deploy" }) }));
    }
    if (series.id === alertsId) {
        if (point.alertData) {
            //Alert tool tip so populate with the correct information related to the budget alert.
            tooltipTitle = _jsx("div", { children: "Budget Alert" });
            let changeColor = "neutral";
            let differenceSuffix = "% Over";
            if (point.alertData.threshold.type === "rate_of_change") {
                differenceSuffix = "% Change";
                if (point.alertData.status === "under") {
                    changeColor = "good";
                }
                else if (point.alertData.status === "over") {
                    changeColor = "bad";
                }
                else if (point.alertData.status === "no_cross") {
                    changeColor = "neutral";
                }
            }
            else if (point.alertData.threshold.type === "absolute") {
                if (point.alertData.status === "under") {
                    differenceSuffix = "% Under";
                    changeColor = "good";
                }
                else if (point.alertData.status === "over") {
                    differenceSuffix = "% Over";
                    changeColor = "bad";
                }
            }
            budgetPercentage = (_jsx("div", { className: style["budget"], children: _jsx(Change, { value: point.alertData.difference.percentage, suffix: differenceSuffix, changeType: changeColor, noArrow: true, smallMode: true }) }));
        }
    }
    if (budget === null || budget === void 0 ? void 0 : budget.budget_id) {
        actionsList.push(_jsx(ChartTooltipAction, { icon: "eye", children: _jsx("a", { href: `${window.accountUrl}/budget/${budget.budget_id}/`, children: "View Budget" }) }));
    }
    return (_jsxs(Tooltip, { footer: actionsList, children: [tooltipTitle, appendTimezone(timestampToDateString(point.x / 1000)), Number(point.num) > 0 && _jsx("div", { children: `${String(point.num)} ${series.numLabel}` }), _jsx(TooltipContent, { point: point, series: series, budget: budget }), budgetPercentage] }));
}
function TooltipContent(props) {
    const { budget, point, series } = props;
    if (series.id === deploysId) {
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: classNames(style["name"], style["deploy"]), children: point.options.name }), _jsx("div", { className: style["description"], children: point.options.description })] }));
    }
    if (series.id === notesId) {
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: classNames(style["name"], style["note"]), children: point.options.name }), _jsx("div", { className: style["description"], children: point.options.description })] }));
    }
    if (series.id === alertsId && point.alertData) {
        const color = (() => {
            if (point.alertData.status === "under") {
                return "var(--good-color)";
            }
            else if (point.alertData.status === "over") {
                return "var(--bad-color)";
            }
            return "var(--medium-emphasis)";
        })();
        return (_jsx(Metric, { color: color, title: point.alertData.series_name, value: `${point.alertData.value}${point.alertData.metric.units}`, budget: `${point.alertData.threshold.value}${point.alertData.metric.units}` }));
    }
    const budgetContent = (() => {
        var _a;
        const threshold = budget === null || budget === void 0 ? void 0 : budget.threshold;
        const metric = (_a = budget === null || budget === void 0 ? void 0 : budget.metric) === null || _a === void 0 ? void 0 : _a.qualified;
        if (threshold && metric === series.metricQualified) {
            if (threshold.type === "absolute") {
                return `${String(threshold.value)}${series.suffix}`;
            }
            else if (threshold.type === "rate_of_change") {
                return `${String(threshold.value)}%`;
            }
        }
    })();
    // Get the point color from the options, since `point.color` is the color that Highcharts
    // rendered, not the color that we defined in the series.
    const pointColor = point.options.color;
    return (_jsx(Metric, { color: pointColor || series.color, title: series.name, value: `${formatNumber(point.y)}${series.suffix}`, budget: budgetContent }));
}
